/** Only providers already admitted by LearningContent. No arbitrary iframe SDK or URL. */
export function learningExternalVideo(source, hostOrigin, position = 0) {
  let input;
  try { input = new URL(source?.url); } catch (_) { return null; }
  if (input.protocol !== 'https:' || input.username || input.password || (input.port && input.port !== '443')) return null;
  const host = input.hostname;
  let provider; let id;
  if (source.provider === 'youtube' && ['youtube.com', 'www.youtube.com', 'youtu.be', 'www.youtube-nocookie.com'].includes(host)) {
    provider = 'youtube'; id = host === 'youtu.be' ? input.pathname.slice(1) : input.searchParams.get('v') || input.pathname.split('/').filter(Boolean).at(-1);
  } else if (source.provider === 'vimeo' && ['vimeo.com', 'www.vimeo.com', 'player.vimeo.com'].includes(host)) {
    provider = 'vimeo'; id = input.pathname.split('/').filter(Boolean).at(-1);
  } else if (source.provider === 'iframe' && host === 'www.youtube-nocookie.com' && /^\/embed\/[\w-]{11}$/.test(input.pathname)) {
    provider = 'youtube'; id = input.pathname.split('/').at(-1);
  } else if (source.provider === 'iframe' && host === 'player.vimeo.com' && /^\/video\/\d+$/.test(input.pathname)) {
    provider = 'vimeo'; id = input.pathname.split('/').at(-1);
  } else if ((source.provider === 'rutube' && ['rutube.ru', 'www.rutube.ru'].includes(host)) || (source.provider === 'iframe' && host === 'rutube.ru')) {
    const pattern = source.provider === 'iframe' ? /^\/play\/embed\/([a-f0-9]{32})\/?$/i : /^\/(?:play\/embed|video(?:\/private)?)\/([a-f0-9]{32})\/?$/i;
    const match = input.pathname.match(pattern);
    if (!match) return null;
    provider = 'rutube'; id = match[1].toLowerCase();
  } else return null;
  if (!(provider === 'youtube' ? /^[\w-]{11}$/ : provider === 'rutube' ? /^[a-f0-9]{32}$/ : /^\d+$/).test(id || '')) return null;
  let origin;
  try { origin = new URL(hostOrigin); } catch (_) { return null; }
  if (!['http:', 'https:'].includes(origin.protocol) || origin.origin !== hostOrigin) return null;
  const start = Number.isFinite(position) ? Math.max(0, Math.min(604800, Math.floor(position))) : 0;
  const url = new URL(provider === 'youtube' ? `https://www.youtube-nocookie.com/embed/${id}` : provider === 'rutube' ? `https://rutube.ru/play/embed/${id}/` : `https://player.vimeo.com/video/${id}`);
  url.searchParams.set('autoplay', provider === 'rutube' ? 'false' : '0');
  if (provider === 'youtube') {
    url.searchParams.set('enablejsapi', '1'); url.searchParams.set('origin', origin.origin);
    url.searchParams.set('playsinline', '1'); if (start) url.searchParams.set('start', String(start));
  } else if (provider === 'rutube') {
    // Keep only the documented private-video key; never forward caller playback controls.
    const keys = input.searchParams.getAll('p');
    if (keys.length > 1 || (keys.length && !/^[a-zA-Z0-9_-]{1,256}$/.test(keys[0]))) return null;
    if (keys.length) url.searchParams.set('p', keys[0]);
    if (start) url.searchParams.set('t', String(start));
  } else {
    const hash = input.searchParams.get('h');
    if (/^[a-zA-Z0-9]+$/.test(hash || '')) url.searchParams.set('h', hash);
  }
  return { provider, id, url: url.href, origin: url.origin, pathname: url.pathname, start };
}

/** Bind messages to this exact frame window, origin and video path. */
export function bindLearningVideoMessages(iframe, descriptor, hostWindow, afterMessage = () => {}) {
  const frameWindow = iframe.contentWindow;
  let stopped = false; let inMessage = false; let serial = 0;
  const current = () => {
    if (stopped || !iframe.isConnected || iframe.contentWindow !== frameWindow) return false;
    try { const url = new URL(iframe.src); return url.origin === descriptor.origin && url.pathname === descriptor.pathname && (descriptor.provider !== 'rutube' || url.href === descriptor.url); } catch (_) { return false; }
  };
  const listener = event => {
    const dispatch = ++serial;
    inMessage = current() && event.origin === descriptor.origin && event.source === frameWindow;
    if (!inMessage) return;
    // The official SDK processes the same dispatch before its documented getters are read.
    queueMicrotask(() => { if (dispatch !== serial) return; inMessage = false; if (current()) afterMessage(); });
  };
  hostWindow.addEventListener('message', listener, true);
  return { current, inMessage: () => inMessage && current(), dispose() { stopped = true; inMessage = false; hostWindow.removeEventListener('message', listener, true); } };
}

const sdkLoads = new WeakMap();
export function loadLearningVideoSdk(provider, hostWindow = window, doc = document) {
  if (!['youtube', 'vimeo'].includes(provider)) return Promise.reject(new Error('Unsupported video provider'));
  const existing = () => provider === 'youtube' ? hostWindow.YT?.Player && hostWindow.YT : hostWindow.Vimeo?.Player && hostWindow.Vimeo;
  if (existing()) return Promise.resolve(existing());
  if (!sdkLoads.has(hostWindow)) sdkLoads.set(hostWindow, new Map());
  const loads = sdkLoads.get(hostWindow);
  if (loads.has(provider)) return loads.get(provider);
  const promise = new Promise((resolve, reject) => {
    const script = doc.createElement('script');
    script.src = provider === 'youtube' ? 'https://www.youtube.com/iframe_api' : 'https://player.vimeo.com/api/player.js';
    script.async = true;
    let complete = false;
    const previous = hostWindow.onYouTubeIframeAPIReady;
    const finish = failed => {
      if (complete) return;
      const api = existing();
      if (!failed && !api) return;
      complete = true; clearTimeout(timer); script.onerror = null; script.onload = null;
      if (provider === 'youtube' && hostWindow.onYouTubeIframeAPIReady === ready) hostWindow.onYouTubeIframeAPIReady = previous;
      if (failed) { script.remove(); reject(new Error('Video player API is unavailable')); } else resolve(api);
    };
    const ready = () => { try { if (typeof previous === 'function') previous(); } finally { finish(false); } };
    if (provider === 'youtube') hostWindow.onYouTubeIframeAPIReady = ready;
    const timer = setTimeout(() => finish(true), 20000);
    script.onerror = () => finish(true); script.onload = () => finish(false);
    doc.head.append(script);
  });
  loads.set(provider, promise);
  promise.catch(() => { if (loads.get(provider) === promise) loads.delete(provider); });
  return promise;
}

/** Rutube's documented JSON postMessage protocol; no SDK, polling or playback commands. */
function mountLearningRutube(iframe, { descriptor, onTime, onError, onReady, hostWindow }) {
  let stopped = false; let failed = false; let ready = false; let started = false; let frameInvalid = false;
  let playing = false; let rollActive = false; let adActive = false;
  let duration = null; let last = null;
  const boundary = bindLearningVideoMessages(iframe, descriptor, hostWindow);
  const fail = () => { if (!stopped && !failed) { failed = true; clearTimeout(readyTimeout); onError(); } };
  const readyTimeout = setTimeout(fail, 30000);
  const advertising = () => rollActive || adActive;
  const types = new Set(['ready', 'init', 'playStart', 'changeState', 'durationChange', 'currentTime', 'rollState', 'adStart', 'adEnd', 'playComplete', 'error'].map(type => `player:${type}`));
  const record = value => value !== null && typeof value === 'object' && !Array.isArray(value);
  const sample = (paused = false, ended = false) => {
    if (last !== null && started && !advertising()) onTime({ seconds: last, paused, ended });
  };
  const listener = event => {
    if (stopped || failed || frameInvalid) return;
    if (!boundary.current()) { frameInvalid = true; return; }
    if (!boundary.inMessage() || typeof event.data !== 'string' || event.data.length > 16384) return;
    let message;
    try { message = JSON.parse(event.data); } catch (_) { return; }
    if (!record(message) || !types.has(message.type) || !record(message.data)) return;
    const { type, data } = message;
    // playerId is a constant shared by Rutube frames, not a video identity.
    for (const key of ['videoId', 'video_id']) {
      if (Object.hasOwn(data, key) && (typeof data[key] !== 'string' || data[key].toLowerCase() !== descriptor.id)) { fail(); return; }
    }
    if (type === 'player:error') { fail(); return; }
    if (type === 'player:ready' || type === 'player:init') {
      if (typeof data.videoId !== 'string') return;
      if (type === 'player:init') { started = false; playing = false; duration = null; last = null; }
      if (!ready) { ready = true; clearTimeout(readyTimeout); onReady(); }
      return;
    }
    // Ads can start before video readiness. Either documented channel can keep time blocked.
    if (type === 'player:rollState') {
      if (!['play', 'complete'].includes(data.state)) return;
      rollActive = data.state === 'play'; playing = false; return;
    }
    if (type === 'player:adStart' || type === 'player:adEnd') {
      adActive = type === 'player:adStart'; playing = false; return;
    }
    if (!ready) return;
    if (type === 'player:playStart') {
      if (typeof data.video_id === 'string') { started = true; playing = !advertising(); }
      return;
    }
    if (advertising()) return;
    if (type === 'player:durationChange') {
      if (Number.isFinite(data.duration) && data.duration > 0 && data.duration <= 604800) duration = data.duration;
    } else if (type === 'player:changeState' && started) {
      if (data.state === 'playing') playing = true;
      else if (data.state === 'paused' || data.state === 'stopped') {
        playing = false; sample(data.state === 'paused', data.state === 'stopped');
        if (data.state === 'stopped') started = false;
      }
    } else if (type === 'player:currentTime' && started && playing) {
      if (duration !== null && Number.isFinite(data.time) && data.time >= 0 && data.time <= duration) { last = data.time; sample(); }
    } else if (type === 'player:playComplete' && started) {
      playing = false; sample(false, true); started = false;
    }
  };
  hostWindow.addEventListener('message', listener);
  return () => {
    if (stopped) return;
    stopped = true; clearTimeout(readyTimeout); boundary.dispose(); hostWindow.removeEventListener('message', listener);
  };
}

/** Official provider APIs; only actual player positions enter the shared progress sink. */
export function mountLearningExternalVideo(iframe, { descriptor, onTime = () => {}, onError = () => {}, onReady = () => {}, hostWindow = window, loadSdk = loadLearningVideoSdk }) {
  if (descriptor.provider === 'rutube') return mountLearningRutube(iframe, { descriptor, onTime, onError, onReady, hostWindow });
  let player = null; let ready = false; let started = false; let seeking = false;
  let stopped = false; let failed = false;
  const subscriptions = [];
  const fail = () => { if (!stopped && !failed) { failed = true; clearTimeout(readyTimeout); onError(); } };
  // This timeout reports a blocked/unavailable embed. It never generates playback time.
  const readyTimeout = setTimeout(fail, 30000);
  const becameReady = () => { ready = true; clearTimeout(readyTimeout); onReady(); };
  const usable = () => !failed && boundary.current();
  const sampleYoutube = () => {
    if (!ready || !usable() || player?.getIframe() !== iframe) return;
    try {
      if (new URL(player.getVideoUrl()).searchParams.get('v') !== descriptor.id) { fail(); return; }
      const state = player.getPlayerState();
      if (state === 1) started = true;
      if (started && [0, 1, 2].includes(state)) {
        const seconds = player.getCurrentTime();
        if (Number.isFinite(seconds) && seconds >= 0) onTime({ seconds, ended: state === 0, paused: state === 2 });
      }
    } catch (_) { fail(); }
  };
  const boundary = bindLearningVideoMessages(iframe, descriptor, hostWindow, () => { if (descriptor.provider === 'youtube') sampleYoutube(); });
  const ownedYoutubeEvent = event => usable() && event.target === player && player?.getIframe() === iframe;
  loadSdk(descriptor.provider, hostWindow, iframe.ownerDocument).then(api => {
    if (!usable()) return;
    if (descriptor.provider === 'youtube') {
      player = new api.Player(iframe, { events: {
        onReady: event => { if (ownedYoutubeEvent(event)) becameReady(); },
        onError: event => { if (ownedYoutubeEvent(event)) fail(); },
      } });
    } else {
      player = new api.Player(iframe);
      const listen = (event, callback) => { subscriptions.push([event, callback]); player.on(event, callback); };
      listen('loaded', event => { if (boundary.inMessage() && String(event?.id) !== descriptor.id) fail(); });
      listen('error', () => { if (boundary.inMessage()) fail(); });
      listen('playing', () => {
        if (!usable() || !boundary.inMessage() || started) return;
        started = true;
        // Vimeo setCurrentTime before first playback would autoplay. Resume only after user play.
        if (descriptor.start > 0) {
          seeking = true;
          player.getDuration().then(duration => {
            if (!usable()) return;
            if (!Number.isFinite(duration) || duration <= 0) throw new Error('Unknown video duration');
            return player.setCurrentTime(Math.min(descriptor.start, Math.max(0, duration - 1)));
          }).then(() => { seeking = false; }).catch(fail);
        }
      });
      for (const event of ['timeupdate', 'pause', 'ended']) listen(event, value => {
        if (!usable() || !boundary.inMessage() || !started || seeking || !Number.isFinite(value?.seconds) || value.seconds < 0) return;
        onTime({ seconds: value.seconds, ended: event === 'ended', paused: event === 'pause' });
      });
      player.ready().then(() => { if (usable()) becameReady(); }).catch(fail);
    }
  }).catch(fail);
  return () => {
    if (stopped) return;
    stopped = true; clearTimeout(readyTimeout); boundary.dispose();
    try {
      subscriptions.forEach(([event, callback]) => player.off(event, callback));
      Promise.resolve(player?.destroy()).catch(() => {});
    } catch (_) { /* A partially loaded provider may already be gone. */ }
  };
}
